// Értesítés-SZÖVEG render: `type` + `data` → cím/törzs (i18n). A legacy `formatNotification()` utódja.
//
// MIÉRT ITT (és nem a szerveren): a feed-sor SZÁNDÉKOSAN nem hordoz kész szöveget (lásd
// `NotificationRowSchema` a contractban) — így (1) a nyelvváltás VISSZAMENŐLEG is hat a már kiküldött
// értesítésekre, (2) a szöveg-javítás nem igényel adat-migrációt.
//
// SZABÁLYOK:
//   • i18n-kulcs: `notify.types.<type>.title` / `.body` (a `type` a katalógus-kulcs — `apps/api/src/notify/
//     events.ts` `NOTIFY_EVENT_TYPES` a forrás-igazság). A `.` az i18next kulcs-szeparátora → a nyelvi fájlban
//     a típus BEÁGYAZOTT objektumként él (`ticket: { assigned: { title, body } }`).
//   • MINDEN interpolációs mezőnek van ÉRTELMES fallbackja → a szövegben SOHA nem jelenik meg `undefined`
//     (vagy kitöltetlen `{{placeholder}}`), akkor sem, ha a `data` hiányos/`null`.
//   • ISMERETLEN `type` (jövőbeli szerver-verzió) → generikus fallback-szöveg; a lista NEM törik meg.
//   • A visszaadott szöveg SIMA SZÖVEG — a hívó SOHA nem renderelheti `dangerouslySetInnerHTML`-lel
//     (a `data` user-adatot hordoz: tárgy, komment-eredetű nevek, …).
import { type TFunction } from 'i18next';

import { type NotificationRow } from './api';

import { formatName } from '@/lib/format-name';

/**
 * A KATALÓGUS tükre (`apps/api/src/notify/events.ts` → `NOTIFY_EVENT_TYPES`). Csak a teszt-lefedettséghez és
 * az ikon-térképhez kell — a render maga NEM zárt listára megy (ismeretlen típus → fallback), hogy egy újabb
 * szerver-verzió típusa se dönthesse meg a harangot.
 *
 * **SZÁNDÉKOSAN NEM TELJES tükör:** a szerver-katalógus `EMAIL_ONLY_TYPES` tételei (`identity.password_reset`,
 * `identity.welcome`, `meeting.created`) KIMARADNAK. Ezekhez sosem készül in-app értesítés (a jelszó-leveleknél a
 * címzett épp nem tud belépni, és a payload nyers beállító-tokent hordoz, ami nem íródhat az Odin
 * `notifications.data`-jába; a meeting-visszaigazolás arról szól, amit a létrehozó épp most rögzített) — tehát a harangban
 * meg sem jelenhetnek, és nincs is `notify.types.*` i18n-kulcsuk. Ne „javítsd" a listát a felvételükkel: a
 * lefedettség-teszt épp azt bizonyítaná, hogy hiányzik hozzájuk a fordítás.
 */
export const NOTIFICATION_TYPES = [
  'ticket.opened',
  'ticket.assigned',
  'ticket.status_changed',
  'ticket.closed',
  'ticket.resolved',
  'ticket.comment.external',
  'ticket.comment.internal',
  // A KOMMENT-REAKCIÓ (emoji) — KIMARADT ebből a tükörből, és ez nem kozmetikai hiba volt: a
  // lefedettség-fal (`render.test.ts`) EZT A LISTÁT járja be, tehát a hiányzó típusra nem is keresett
  // i18n-kulcsot. A harang emiatt a generikus „Értesítés / Új értesítés érkezett" szövegre esett minden
  // reakciónál (user-lelet). Új szerver-típus → ELŐBB ide, csak utána a nyelvi fájlba.
  'ticket.comment.reaction',
  'ticket.access_granted',
  'message.received',
  'message.delivery_truncated',
  'meeting.invited',
  'meeting.rescheduled',
  'meeting.cancelled',
  'meeting.removed',
  'meeting.link_changed',
  'meeting.reminder',
  'asset.requested',
  'asset.approved',
  'asset.rejected',
  'asset.purchased',
  // a jóváhagyás tájékoztatója (ár nélkül) és az igény bírálói módosítása (#116–#118)
  'asset.approved_info',
  'asset.request_updated',
  // Előfizetés / licenc lejárata (napi cron; három fokozat — a `stage` a szöveg-variáns).
  'asset.expiring',
  'payment_card.expiring',
  'payment_card.expired',
  // FÜGGŐ hozzárendelés, aminek az induló dátuma hamarosan esedékes (napi cron — a hozzárendelés-lista
  // figyelem-paneljével AZONOS szabály, `noticeDays`).
  'service_assignment.pending_start_soon',
  // A KÉZZEL indított, HÁTTÉRBEN futó havi számla-generálás visszajelzése a kezdeményezőnek.
  'invoice.generation_finished',
] as const;

export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

/**
 * A render `t`-je — a `useTranslation()` (default `translation` namespace) fordító-fn-je. A modul így PURE és
 * unit-tesztelhető: a teszt a VALÓS i18n-példány `t`-jét adja (a nyelvi fájlok lefedettségét is ellenőrizve).
 */
export type Translate = TFunction<'translation', undefined>;

export interface NotificationText {
  title: string;
  body: string;
}

/**
 * A `data`-ból FELHASZNÁLHATÓ mezők + a hiányzó érték fallback-kulcsa. Csak az itt felsorolt mezők kerülnek
 * interpolációba (allowlist) — így (a) egy váratlan payload-mező nem szivárog a szövegbe, (b) minden
 * placeholderhez GARANTÁLTAN van érték. A `startsInMinutes` szám → stringgé formázzuk.
 */
const FIELD_FALLBACK_KEYS = {
  actorName: 'notify.fallback.actorName',
  ticketNumber: 'notify.fallback.ticketNumber',
  accountName: 'notify.fallback.accountName',
  subject: 'notify.fallback.subject',
  // Az üzenet-csonkolás figyelmeztetés darabszámai (számok → stringgé formázva).
  deliveredCount: 'notify.fallback.deliveredCount',
  droppedCount: 'notify.fallback.droppedCount',
  title: 'notify.fallback.meetingTitle',
  startsAtLabel: 'notify.fallback.startsAtLabel',
  startsInMinutes: 'notify.fallback.startsInMinutes',
  assetName: 'notify.fallback.assetName',
  assetType: 'notify.fallback.assetType',
  team: 'notify.fallback.team',
  netPriceLabel: 'notify.fallback.netPriceLabel',
  rejectReason: 'notify.fallback.rejectReason',
  cardMasked: 'notify.fallback.cardMasked',
  cardExpiryLabel: 'notify.fallback.cardExpiryLabel',
  expiryLabel: 'notify.fallback.expiryLabel',
  clientName: 'notify.fallback.clientName',
  // A komment-reakció JELE (`ticket.comment.reaction`). Fallback kell hozzá is: a `data` a szerver felől
  // jön, és egy mező nélküli régi sor különben `undefined`-ot írna a szövegbe.
  emoji: 'notify.fallback.emoji',
  // A FÜGGŐ hozzárendelés hamarosan-indul ÖSSZESÍTŐJÉHEZ (`service_assignment.pending_start_soon`): a sor a
  // DARABSZÁMOT és a legkorábbi indulást mondja ki — a tétel-lista szándékosan nem kerül az in-app `data`-ba
  // (a levél sorolja fel, l. `notify/events.ts`). A `serviceName`/`planName` a RÉGI, tétel-szintű sorok
  // miatt marad az allowlistán: azok már kimentek, és visszamenőleg nem írhatók át.
  serviceName: 'notify.fallback.serviceName',
  planName: 'notify.fallback.planName',
  startDate: 'notify.fallback.startDate',
  firstStartDate: 'notify.fallback.startDate',
  count: 'notify.fallback.count',
  // A számla-generálás visszajelzéséhez (`invoice.generation_finished`). A DARABSZÁMOK 0 értéke VALÓDI
  // („0 kihagyás" a jó hír) — a fallback csak a hiányzó/érvénytelen mezőre szól, a `displayValue` a 0-t
  // rendes értékként adja vissza.
  month: 'notify.fallback.month',
  invoices: 'notify.fallback.count',
  items: 'notify.fallback.count',
  skipped: 'notify.fallback.count',
} as const;

/** Az eszköz-típus kulcs-alakja (`desktop_pc`) — csak ez fordítódik címkére (lásd `toParams`). */
const ASSET_TYPE_KEY = /^[a-z][a-z_]*$/;

/** Csak `[a-z0-9_.]` típus-kulcs mehet i18n-kulcsba (a `:` az i18next namespace-szeparátora → kizárva). */
const SAFE_TYPE = /^[a-z0-9_]+(\.[a-z0-9_]+)*$/i;

/** Egy payload-érték → megjelenítendő string, vagy `null` ha nincs használható érték. */
function displayValue(raw: unknown): string | null {
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed === '' ? null : trimmed;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  return null;
}

/** A katalógus `{ hu, en }` neve a nyelv szerint (az `en` hiányában a magyar); más alak → `null`. */
function catalogName(raw: unknown, locale: string): string | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const hu = displayValue((raw as { hu?: unknown }).hu);
  const en = displayValue((raw as { en?: unknown }).en);
  if (locale.toLowerCase().startsWith('en') && en !== null) return en;
  return hu;
}

/**
 * A `data` → interpolációs paraméterek (minden allowlistelt mező kitöltve: érték VAGY lokalizált fallback).
 *
 * Az AKTOR-NÉV külön szabály: ha a payload hordozza a név-részeket, a sorrendet ITT rendeljük a NYELV
 * szerint (`formatName` — magyarul vezetéknév elöl). Enélkül az Odin összefűzött `displayName`-je
 * (keresztnév-elöl) magyar felületen fordítva jelent meg (user-lelet). A régi sorokon nincsenek
 * részek → a `actorName` a fallback, tehát a visszamenőleges viselkedés változatlan.
 */
function toParams(
  t: Translate,
  data: Record<string, unknown> | null,
  locale: string,
): Record<string, string> {
  const params: Record<string, string> = {};
  for (const [field, fallbackKey] of Object.entries(FIELD_FALLBACK_KEYS)) {
    params[field] = displayValue(data?.[field]) ?? t(fallbackKey);
  }
  // Az ESZKÖZ-TÍPUS a buszon KULCS (`laptop`, `subscription`) — a felület meglévő címkéjére fordítjuk (a lista és a
  // form ugyanezt mutatja); ismeretlen kulcsnál a nyers érték marad. A kulcs-alak szűrése miatt payload-érték nem
  // tud tetszőleges i18n-kulcsot címezni.
  // a katalógus kétnyelvű neve az elsőbbséget élvezi (egyedi típus is így jelenik meg); hiányában a kulcs-szótár
  const catalogLabel = catalogName(data?.assetTypeLabel, locale);
  const typeKey = displayValue(data?.assetType);
  if (catalogLabel !== null) {
    params.assetType = catalogLabel;
  } else if (typeKey !== null && ASSET_TYPE_KEY.test(typeKey)) {
    params.assetType = t(`assets.typeLabel.${typeKey}`, { defaultValue: typeKey });
  }
  const given = displayValue(data?.actorGivenName);
  const family = displayValue(data?.actorFamilyName);
  if (given !== null && family !== null) {
    params.actorName = formatName({ givenName: given, familyName: family }, locale);
  }
  return params;
}

/**
 * Egy értesítés cím/törzs párja. Hibát NEM dob: ismeretlen/érvénytelen típusnál a generikus fallback-szöveget
 * adja, hiányos `data` esetén a mező-fallbackokat interpolálja.
 */
export function renderNotification(
  t: Translate,
  type: string,
  data: Record<string, unknown> | null,
  /** A megjelenítés nyelve — CSAK a névsorrendhez kell (a szövegeket a `t` már a nyelvén adja). */
  locale: string,
): NotificationText {
  const params = toParams(t, data, locale);
  const genericTitle = t('notify.fallback.title');
  const genericBody = t('notify.fallback.body');
  if (!SAFE_TYPE.test(type)) return { title: genericTitle, body: genericBody };

  const neutralTitle = t(`notify.types.${type}.title`, { ...params, defaultValue: genericTitle });
  const neutralBody = t(`notify.types.${type}.body`, { ...params, defaultValue: genericBody });
  // Variáns-finomítás (ha van): a `…_<variáns>` kulcs, ha létezik; különben a semleges szöveg.
  const variant = textVariant(type, data);
  const title = variant
    ? t(`notify.types.${type}.title_${variant}`, { ...params, defaultValue: neutralTitle })
    : neutralTitle;
  const body = variant
    ? t(`notify.types.${type}.body_${variant}`, { ...params, defaultValue: neutralBody })
    : neutralBody;
  // Végső biztonsági háló: üres/whitespace fordítás (rossz nyelvi fájl) se hagyjon üres sort a panelben.
  return {
    title: title.trim() === '' ? genericTitle : title,
    body: body.trim() === '' ? genericBody : body,
  };
}

/**
 * VARIÁNS-kulcs: néhány típusnál a `data` egy mezője finomítja a szöveget, mert a legacy is külön mondatot
 * adott — a ticket-státusz „vizsgálat alatt" vs „újranyitva" esete. A variáns egy **fix, allowlistelt**
 * érték-készletből jön (nem a nyers payloadból), így nem tud i18n-kulcsot injektálni; ha nincs hozzá
 * fordítás, a semleges szöveg a fallback. Új variáns bevezetése = itt egy sor + a két nyelvi kulcs.
 */
function textVariant(type: string, data: Record<string, unknown> | null): string | null {
  if (type === 'ticket.status_changed') {
    const status = displayValue(data?.status);
    return status === 'in_progress' || status === 'reopened' ? status : null;
  }
  if (type === 'asset.expiring') {
    // A lejárat FOKOZATA (30 nap · 7 nap · elérte) — ismeretlen érték → a semleges szöveg.
    const stage = displayValue(data?.stage);
    return stage === 'month' || stage === 'week' || stage === 'due' ? stage : null;
  }
  if (type === 'message.delivery_truncated') {
    // A HÁROM PLAFON HÁROM külön teendő, és a `droppedCount` MÉRTÉKEGYSÉGE is más mindháromnál:
    //   `recipient_cap`    → CÍMZETT-darabszám (szűkítsd a címzést),
    //   `account_scan_cap` → cél-FIÓK-darabszám (küldd több részletben),
    //   `pivot_page_cap`   → a fel sem olvasott címzett-DIMENZIÓK száma (a címzés visszaolvasása állt le).
    // A semleges szöveg egyiket sem hazudja — ismeretlen ok esetén az megy.
    const reason = displayValue(data?.reason);
    return reason === 'recipient_cap' ||
      reason === 'account_scan_cap' ||
      reason === 'pivot_page_cap'
      ? reason
      : null;
  }
  return null;
}

/**
 * A harang-badge felirata: 0 → a hívó NEM rendereli a plakettet; 99 fölött „99+". A BFF a számolást 100-nál
 * állítja meg (`NotificationListResponseSchema`), tehát a 100 = „100 vagy több" → „99+"; a pontosan 99
 * olvasatlan viszont „99"-ként pontos (ezért `> 99` és nem `>= 99`).
 */
export function badgeLabel(unreadCount: number): string {
  return unreadCount > 99 ? '99+' : String(unreadCount);
}

/** Kényelmi burkoló a feed-sorra. */
export function renderNotificationRow(
  t: Translate,
  row: NotificationRow,
  locale: string,
): NotificationText {
  return renderNotification(t, row.type, row.data, locale);
}
